"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { ArrowRight } from "lucide-react";
import { Button, Dialog, DialogContent } from "@/components/ui";
import { domId } from "@/lib/dom-id";
import BannerStarsIcon from "@/components/icons/banner-stars-icon";
import { useGlobalStore } from "@/store/global";
import { useWorkspaceStore } from "@/store/workspace/workspace-store";

/**
 * Connector Banner Component
 *
 * Displays a promotional banner encouraging users to explore OXU Connector features.
 * Features:
 * - Eye-catching gradient background
 * - Call-to-action buttons (Open Copilot, See Demo)
 * - Decorative background SVG pattern
 */
export default function ConnectorBanner() {
  const t = useTranslations("ConnectorBanner");
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  const { selectedWorkspace } = useWorkspaceStore();
  const [isInplaceCopilot, bridgeAppId] = useGlobalStore((s) => [s.bridge.isInplaceCopilot, s.bridge.appId]);

  const handleOpenCopilot = () => {
    if (isInplaceCopilot) {
      window.parent.postMessage(
        {
          namespace: "WS_BRIDGE",
          from: "app",
          app_id: bridgeAppId,
          type: "APP_NAVIGATE",
          version: "2.0",
          payload: { target: "app" },
        },
        "*",
      );
      return;
    }

    const copilotUrl = process.env.NEXT_PUBLIC_COPILOT_URL;
    if (copilotUrl) {
      // Build the full URL with workspace_id
      const url = new URL(copilotUrl);
      url.pathname = "/chat/";
      if (selectedWorkspace?.workspace_id) {
        url.searchParams.set("workspace_id", selectedWorkspace.workspace_id);
      }
      window.open(url.toString(), "_blank");
    } else {
      console.warn("NEXT_PUBLIC_COPILOT_URL is not defined");
    }
  };

  const handleSeeDemo = () => {
    setIsDemoOpen(true);
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl p-8"
      style={{
        background: 'linear-gradient(90deg, #E0F2FE 0%, #DBEAFE 100%)',
        backgroundImage: 'url(/connector_banner.svg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Stars decoration on the right */}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 opacity-5">
        <BannerStarsIcon />
      </div>

        {/* Content */}
      <div className="relative z-10 flex items-center justify-between">
        <div className="flex-1">
          <h2 className="text-lg font-semibold leading-6 text-slate-900">
            {t("title", { defaultValue: "Get the most out of" })}{" "}
            <span className="text-gradient-primary">
              {t("productName", { defaultValue: "OXU Connector" })}
            </span>
          </h2>
          <p className="mt-2 text-sm font-normal leading-5 text-slate-700">
            {t(
              "description",
              {
                defaultValue:
                  "Watch how OXU Connector turns API documentation into intelligent actions.",
              },
            )}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <Button
            data-testid={domId("btn", "banner-open-copilot")}
            type="button"
            onClick={handleOpenCopilot}
            variant="unstyled"
            className="btn-gradient-primary rounded-lg px-6 py-2.5 text-xs font-semibold uppercase leading-4 shadow-md"
          >
            {t("openCopilot", { defaultValue: "OPEN COPILOT" })}
          </Button>
          <Button
            data-testid={domId("btn", "banner-see-demo")}
            type="button"
            onClick={handleSeeDemo}
            variant="unstyled"
            className="btn-gradient-border inline-flex items-center gap-2 rounded-lg bg-white px-6 py-2.5 text-xs font-semibold uppercase leading-4"
          >
            <span className="text-gradient-primary">{t("seeDemo", { defaultValue: "SEE DEMO" })}</span>
            <ArrowRight className="h-4 w-4" style={{ color: '#713D98' }} />
          </Button>
        </div>
      </div>

      {/* Demo Modal */}
      <Dialog open={isDemoOpen} onOpenChange={setIsDemoOpen}>
        <DialogContent className="max-w-[90vw] w-full h-[90vh] p-0">
          <div className="w-full h-full">
            <iframe
              data-testid={domId("fra", "banner-demo")}
              src="https://app.supademo.com/embed/cmlenk0vv00pv1m0jn5liks7i"
              className="w-full h-full border-0 rounded-lg"
              title="OXU Connector Demo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
